import { router } from 'expo-router';
import React from 'react';
import { View, StyleSheet, Linking } from 'react-native';

import { Button, Dialog, Portal, Text, IconButton } from '@/components/ui';

interface NotificationModalProps {
	visible: boolean;
	onDismiss: () => void;
	title: string;
	body: string;
	type?: 'update' | 'workorder' | 'general';
	workOrderId?: string;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
	visible,
	onDismiss,
	title,
	body,
	type = 'general',
	workOrderId,
}) => {
	const getIconName = () => {
		switch (type) {
			case 'update':
				return 'download-outline';
			case 'workorder':
				return 'construct-outline';
			case 'general':
			default:
				return 'notifications-outline';
		}
	};

	const getTypeColor = () => {
		switch (type) {
			case 'update':
				return '#ff9800';
			case 'workorder':
				return '#2196f3';
			case 'general':
			default:
				return '#4caf50';
		}
	};

	return (
		<Portal>
			<Dialog visible={visible} onDismiss={onDismiss}>
				<Dialog.Title>
					<View style={styles.titleContainer}>
						<IconButton
							icon={getIconName()}
							iconColor={getTypeColor()}
							size={24}
							style={styles.icon}
						/>
						<Text style={styles.titleText}>{title}</Text>
					</View>
				</Dialog.Title>
				<Dialog.Content>
					<View style={styles.contentContainer}>
						<Text style={styles.bodyText}>{body}</Text>

						{type === 'update' && (
							<View style={styles.infoBox}>
								<Text style={styles.infoText}>
									Tap &quot;Open TestFlight&quot; to update the app with the latest features and bug fixes.
								</Text>
							</View>
						)}

						{type === 'workorder' && (
							<View style={styles.infoBox}>
								<Text style={styles.infoText}>
									This notification is related to a specific work order. Check your work orders list for details.
								</Text>
							</View>
						)}
					</View>
				</Dialog.Content>
				<Dialog.Actions>
					<Button onPress={onDismiss}>
						Dismiss
					</Button>
					{type === 'update' && (
						<Button
							onPress={() => {
								// Open TestFlight
								const testflightUrl = 'itms-beta://';
								Linking.openURL(testflightUrl).catch(err => {
									console.error('Failed to open TestFlight:', err);
									// Could show an error message here
								});
								onDismiss();
							}}
							mode="contained"
						>
							Open TestFlight
						</Button>
					)}
					{type === 'workorder' && workOrderId && (
						<Button
							onPress={() => {
								// Navigate to specific work order
								router.push({
									pathname: '/work-orders/[id]',
									params: { id: workOrderId },
								});
								onDismiss();
							}}
							mode="contained"
						>
							View Work Order
						</Button>
					)}
					{type === 'workorder' && !workOrderId && (
						<Button
							onPress={() => {
								// Navigate to work orders list
								router.push('/work-orders');
								onDismiss();
							}}
							mode="contained"
						>
							View Work Orders
						</Button>
					)}
				</Dialog.Actions>
			</Dialog>
		</Portal>
	);
};

const styles = StyleSheet.create({
	titleContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	icon: {
		margin: 0,
		marginRight: 8,
	},
	titleText: {
		fontSize: 18,
		fontWeight: '600',
		flex: 1,
	},
	contentContainer: {
		paddingTop: 8,
	},
	bodyText: {
		fontSize: 16,
		lineHeight: 24,
		color: '#333',
		marginBottom: 16,
	},
	infoBox: {
		backgroundColor: '#f5f5f5',
		padding: 12,
		borderRadius: 8,
		borderLeftWidth: 4,
		borderLeftColor: '#2196f3',
	},
	infoText: {
		fontSize: 14,
		color: '#666',
		fontStyle: 'italic',
	},
});
